// B103 Phase 2 — Household-scope helpers.
//
// Pure functions, no UI. No callers in this commit (Phase 4 swaps aggregation
// sites). See ARCHITECTURE.md §13 + BACKLOG B103 for the design and
// derivation rules.
//
// Phase 2 must not change any user-visible behaviour: every existing
// aggregation site continues to use ownershipFactor as today. These helpers
// exist to be wired in by Phase 4.

import type { PropertyRecord } from "../types/portfolio";
import type { Structure, InvestorProfile } from "../types/entities";
import { annualLoanInterest, filterDeductibleExpenseRows } from "./calculations";

// ─── deriveCoOwnersForMode (Phase 3 — UI helper) ─────────────────────────────

export type OwnershipMode = "sole" | "joint_tenants" | "tenants_in_common";
export interface CoOwnerEntry {
  householdMemberId: string;
  sharePercent: number;
}
export interface CoOwnersDerivation {
  coOwners: CoOwnerEntry[];
  ownershipPercent: number;
}

/**
 * Pure helper used by the PropertyDetailPage ownership editor when the user
 * changes ownershipMode. Derives the new `coOwners` array AND the
 * back-compat `ownershipPercent` value so all 17 pre-Phase-4 read sites
 * (DashboardPage, TaxPage, etc.) keep behaving correctly until Phase 4
 * swaps them onto `householdFactorOf`.
 *
 * Transitions covered:
 * - sole / undefined target → reset coOwners to [], ownershipPercent = 100
 * - joint_tenants target → equal split among (primary + existing coOwners);
 *   primary's share = `100 / (coOwners.length + 1)`, each coOwner gets the
 *   same. Round to integer percentages (acceptable for UX; tests use
 *   `toBeCloseTo` for the 1/3 case).
 * - tenants_in_common target → preserve existing coOwners' sharePercents;
 *   primary takes the residual `100 − Σ(sharePercent)`, clamped to ≥0.
 *
 * Legacy migration: when `currentMode === undefined` (pre-Phase-1 data) and
 * `currentOwnershipPercent < 100` and there are no coOwners yet, seed a
 * single placeholder coOwner with the residual share so the user can pick
 * the household member. This lets the editor migrate a 50/50 legacy
 * property to JT/TIC in one step rather than requiring a manual "+ add
 * co-owner" click after mode selection.
 */
export function deriveCoOwnersForMode(
  currentMode: OwnershipMode | undefined,
  currentCoOwners: CoOwnerEntry[],
  newMode: OwnershipMode | undefined,
  currentOwnershipPercent: number,
): CoOwnersDerivation {
  if (newMode === "sole" || newMode === undefined) {
    return { coOwners: [], ownershipPercent: 100 };
  }

  const cos = currentCoOwners ?? [];
  const isLegacyMigration =
    currentMode === undefined &&
    cos.length === 0 &&
    currentOwnershipPercent < 100;

  if (newMode === "joint_tenants") {
    if (isLegacyMigration) {
      // Seed one placeholder co-owner equal to the missing share. JT
      // semantics: primary + co-owner = 50/50 (regardless of the legacy
      // ownershipPercent value, which may have been entered as 50 by the
      // user OR something different that JT then rationalises).
      return {
        coOwners: [{ householdMemberId: "", sharePercent: 50 }],
        ownershipPercent: 50,
      };
    }
    const totalHolders = cos.length + 1;
    const equalPct = Math.round(100 / totalHolders);
    return {
      coOwners: cos.map((c) => ({ ...c, sharePercent: equalPct })),
      ownershipPercent: equalPct,
    };
  }

  // newMode === "tenants_in_common"
  if (isLegacyMigration) {
    // Seed one placeholder co-owner with the residual share so the
    // legacy `ownershipPercent` survives the migration unchanged.
    return {
      coOwners: [
        {
          householdMemberId: "",
          sharePercent: Math.max(0, 100 - currentOwnershipPercent),
        },
      ],
      ownershipPercent: currentOwnershipPercent,
    };
  }
  const total = cos.reduce((s, c) => s + (c.sharePercent ?? 0), 0);
  return {
    coOwners: cos,
    ownershipPercent: Math.max(0, 100 - total),
  };
}

// ─── isHouseholdShared ───────────────────────────────────────────────────────

/**
 * Returns true when a property's economics are shared with a partner-type
 * household member — i.e. the household pays/receives the FULL property
 * cashflow together regardless of `ownershipPercent`.
 *
 * Personal entity:
 * - ownershipMode === "joint_tenants" → true
 * - ownershipMode === "tenants_in_common" + at least one coOwner → true
 * - ownershipMode === "sole" → false
 * - ownershipMode unset (legacy) + ownershipPercent < 100 + a spouse/partner
 *   relationship exists on the profile → true (best-effort backward compat)
 *
 * Trust entity:
 * - any beneficiary linked to a partner-type household member, either via
 *   the Phase-1 `householdMemberId` link or a case-insensitive name match
 *   against the partner's `preferredName`/`fullLegalName` → true
 *
 * SMSF entity:
 * - `memberNames` includes a partner-type household member's name (case
 *   insensitive) → true
 *
 * All other cases → false.
 */
export function isHouseholdShared(
  property: PropertyRecord,
  structure: Structure | undefined,
  profile: InvestorProfile,
): boolean {
  // B106 — `"Personal"` is an INTERNAL DISPATCH SENTINEL here, NOT an entity
  // type. The entityType union no longer includes "Personal" (LESSONS L40).
  // When `structure` is undefined (personal-name property — structureId is
  // null), we fall back to "Personal" as the dispatch token for the
  // personal-branch logic below. Logic outside this function reads the
  // truth from `isPersonalProperty(p)` in calculations.ts.
  const entityType = structure?.entityType ?? "Personal";
  // Issue 3 (Day 52): coerce missing memberType to partner. The data model
  // has only two kinds of household member — partners and dependents — so a
  // member without an explicit memberType is a partner by definition.
  // Defends against future code paths (CSV import, admin tools) that might
  // insert members without setting memberType. Backfilled live data via
  // `backfill_household_member_type_partner` migration; this guard prevents
  // recurrence at the consumer layer.
  const partners = (profile.householdMembers ?? []).filter(
    (m) => m.memberType !== "dependent",
  );

  if (entityType === "Personal") {
    if (property.ownershipMode === "joint_tenants") return true;
    if (property.ownershipMode === "tenants_in_common") {
      return (property.coOwners?.length ?? 0) > 0;
    }
    if (property.ownershipMode === "sole") return false;
    // Legacy fallback — no ownershipMode set.
    const hasSpouseRelation = (profile.relationships ?? []).some(
      (r) => r.type === "spouse" || r.type === "partner",
    );
    return (property.ownershipPercent ?? 100) < 100 && hasSpouseRelation;
  }

  if (entityType === "Trust") {
    if (partners.length === 0) return false;
    const beneficiaries = structure?.beneficiaries ?? [];
    const partnerIds = new Set(partners.map((p) => p.id));
    const partnerNames = new Set(
      partners
        .flatMap((p) => [
          (p.preferredName ?? "").toLowerCase(),
          (p.fullLegalName ?? "").toLowerCase(),
        ])
        .filter((n) => n.length > 0),
    );
    return beneficiaries.some((b) => {
      if (b.householdMemberId && partnerIds.has(b.householdMemberId)) return true;
      const name = b.name?.toLowerCase() ?? "";
      return name.length > 0 && partnerNames.has(name);
    });
  }

  if (entityType === "SMSF") {
    if (partners.length === 0) return false;
    const lowerNames = (structure?.memberNames ?? []).map((n) => n.toLowerCase());
    return partners.some((p) => {
      const preferred = (p.preferredName ?? "").toLowerCase();
      const legal = (p.fullLegalName ?? "").toLowerCase();
      return (
        (preferred.length > 0 && lowerNames.includes(preferred)) ||
        (legal.length > 0 && lowerNames.includes(legal))
      );
    });
  }

  return false;
}

// ─── householdFactorOf ───────────────────────────────────────────────────────

/**
 * The factor to apply to per-property gross figures when aggregating in
 * household scope. Returns 1.0 for shared properties (the household pays/owns
 * the full thing) and the user's ownership share otherwise.
 *
 * Mirrors the existing `ownershipFactorOf(p) = (ownershipPercent ?? 100) / 100`
 * pattern but adds the household-shared shortcut. Phase 4 swaps aggregation
 * sites onto this when the user picks Household scope.
 */
export function householdFactorOf(
  property: PropertyRecord,
  structure: Structure | undefined,
  profile: InvestorProfile,
): number {
  if (isHouseholdShared(property, structure, profile)) return 1.0;
  return (property.ownershipPercent ?? 100) / 100;
}

// ─── lossAllocationByMember ──────────────────────────────────────────────────

export interface LossAllocation {
  householdMemberId: string; // "primary" or HouseholdMember.id
  share: number;             // 0..1 — fraction of the loss
  allocatedLoss: number;     // loss × share
}

/**
 * Splits a per-property rental loss across household members for tax
 * allocation. Used by Phase 4 to compute per-adult tax saving when the
 * user has the Tax page person picker set to Household.
 *
 * Returns [] for Trust, SMSF, and Company entities (L39 — losses are
 * quarantined inside the entity by ATO rule and cannot be allocated to
 * individual taxpayers at the rental-loss layer).
 *
 * **B120 invariant (defence in depth):** the L39 guard below is the SOLE
 * gate for trust/SMSF/company losses in this helper. `redistributionMode`
 * is intentionally NOT consulted here. ATO ground (ITAA 1936 Schedule 2F
 * + ITAA 1997 Div 36): trust losses cannot be distributed to beneficiaries
 * regardless of trust deed wording or `redistributionMode` configuration.
 * `redistributionMode = 'redistribute'` is a valid configuration for
 * positive surplus distributions only (consumed in calculations.ts
 * `portfolioFundingSummary` line ~1057, gated by `sCashflow > 0`); it must
 * never be allowed to influence loss allocation. Any future refactor that
 * introduces a `redistributionMode` branch in this helper MUST gate it on
 * a positive-cashflow check first. See `docs/audits/B120-audit.md` and
 * the B120 test block in `householdScope.test.ts`.
 *
 * Personal entity allocation:
 * - sole, or 100% ownership, or unknown shape → all loss to "primary"
 * - joint_tenants → equal split among (primary + coOwners). Total holders
 *   = coOwners.length + 1; each gets `loss / total`.
 * - tenants_in_common → split per coOwners[i].sharePercent; primary takes
 *   the residual `100 − sum(coOwners.sharePercent)` percent.
 * - legacy (no ownershipMode + ownershipPercent < 100 + spouse-type
 *   relationship) → 50/50 between primary and the spouse household member.
 * - legacy (no ownershipMode + ownershipPercent < 100 + no spouse known) →
 *   primary takes the full loss (the missing share is an unknown party
 *   we can't allocate to without speculation).
 */
export function lossAllocationByMember(
  property: PropertyRecord,
  structure: Structure | undefined,
  profile: InvestorProfile,
  loss: number,
): LossAllocation[] {
  // B106 — internal dispatch sentinel, not an entity type. See note on the
  // matching line in isHouseholdShared above. Personal-name properties hit
  // the "Personal" branch via the `?? "Personal"` fallback when structure
  // is undefined; the legal SSOT for personal-name is isPersonalProperty(p).
  const entityType = structure?.entityType ?? "Personal";

  // L39 — Trust / SMSF / Company losses are quarantined inside the entity
  // by ATO rule. Cannot be allocated to individual household members at
  // the rental-loss layer. See LESSONS.md L39 + ARCHITECTURE §13.
  //
  // B120 — `structure.redistributionMode` is INTENTIONALLY NOT CONSULTED
  // here. Trust loss quarantine is unconditional under ITAA 1936 Schedule
  // 2F + ITAA 1997 Div 36, regardless of trust-deed redistribute clauses.
  // Tested in the "B120 — redistribution_mode enforcement" describe block.
  // Source: https://www.ato.gov.au/businesses-and-organisations/trusts/managing-trusts/trust-losses
  if (entityType === "Trust" || entityType === "SMSF" || entityType === "Company") {
    return [];
  }

  // Personal entity from here on.
  if (property.ownershipMode === "tenants_in_common") {
    const coOwners = property.coOwners ?? [];
    const totalCoSharePct = coOwners.reduce(
      (s, c) => s + (c.sharePercent ?? 0),
      0,
    );
    const primaryShare = Math.max(0, 100 - totalCoSharePct) / 100;
    const allocations: LossAllocation[] = [];
    if (primaryShare > 0) {
      allocations.push({
        householdMemberId: "primary",
        share: primaryShare,
        allocatedLoss: loss * primaryShare,
      });
    }
    for (const co of coOwners) {
      const share = (co.sharePercent ?? 0) / 100;
      if (share > 0) {
        allocations.push({
          householdMemberId: co.householdMemberId,
          share,
          allocatedLoss: loss * share,
        });
      }
    }
    return allocations;
  }

  if (property.ownershipMode === "joint_tenants") {
    const coOwners = property.coOwners ?? [];
    const totalHolders = coOwners.length + 1; // primary is implicit
    const equalShare = 1 / totalHolders;
    const allocations: LossAllocation[] = [
      {
        householdMemberId: "primary",
        share: equalShare,
        allocatedLoss: loss * equalShare,
      },
    ];
    for (const co of coOwners) {
      allocations.push({
        householdMemberId: co.householdMemberId,
        share: equalShare,
        allocatedLoss: loss * equalShare,
      });
    }
    return allocations;
  }

  if (property.ownershipMode === "sole") {
    // B126 — sole owner is not always primary. When coOwners has exactly one
    // entry with sharePercent === 100, that household member is the sole
    // owner (Kordan-pattern: Nicole sole 100%). Falls back to primary only
    // when coOwners is empty/malformed — preserves the legacy default.
    const co = (property.coOwners ?? [])[0];
    if (co && co.sharePercent === 100 && co.householdMemberId) {
      return [{
        householdMemberId: co.householdMemberId,
        share: 1.0,
        allocatedLoss: loss,
      }];
    }
    return [{ householdMemberId: "primary", share: 1.0, allocatedLoss: loss }];
  }

  // Legacy fallback — no ownershipMode set.
  const ownershipPct = property.ownershipPercent ?? 100;
  if (ownershipPct >= 100) {
    return [{ householdMemberId: "primary", share: 1.0, allocatedLoss: loss }];
  }

  const spouseRel = (profile.relationships ?? []).find(
    (r) => r.type === "spouse" || r.type === "partner",
  );
  if (spouseRel) {
    return [
      { householdMemberId: "primary", share: 0.5, allocatedLoss: loss * 0.5 },
      { householdMemberId: spouseRel.toMemberId, share: 0.5, allocatedLoss: loss * 0.5 },
    ];
  }

  // <100% but no spouse known — primary takes the full loss as a
  // conservative best-effort. The missing share belongs to a third party
  // we can't allocate to without speculating.
  return [{ householdMemberId: "primary", share: 1.0, allocatedLoss: loss }];
}

// ─── lensFactorOf (Phase 4b — TaxPage propertyTaxData factor) ────────────────

/**
 * Returns the share factor for a property from a chosen household member's
 * tax lens. Drop-in replacement for `(p.ownershipPercent ?? 100) / 100` in
 * the TaxPage `propertyTaxData` build loop.
 *
 * **Backward-compat invariant:** when `lensMemberId === "primary"`, returns
 * `(ownershipPercent ?? 100) / 100` — identical to the legacy formula.
 * Pre-Phase-4b TaxPage behaviour is unchanged for the default lens.
 *
 * **Per-person semantics** (when lens is a non-primary member): uses
 * `lossAllocationByMember(p, structure, profile, 1)` to find the member's
 * share. Trust / SMSF / Company entities → factor `0` (loss quarantined
 * per L39). Personal entity factor follows the property's `ownershipMode`:
 * sole = 0 for non-primary; JT = `1/(coOwners.length + 1)`; TIC = the
 * matching coOwner's `sharePercent / 100`; legacy fallback splits 50/50
 * between primary and the spouse (if a spouse-type relationship exists).
 *
 * Used by the TaxPage person picker to drive every share-adjusted figure
 * in `propertyTaxData` (rent, opex, interest, depreciation, borrowing
 * costs). Downstream totals and per-property tax-saving values then
 * automatically reflect the lens member's allocated share.
 */
export function lensFactorOf(
  property: PropertyRecord,
  structure: Structure | undefined,
  profile: InvestorProfile,
  lensMemberId: string,
): number {
  // B126 — sole-by-non-primary path. When ownershipMode === "sole" and
  // coOwners has exactly one entry with 100%, the sole owner is that
  // household member (not primary). Route through lossAllocationByMember
  // for both lenses so primary correctly returns 0 and the coOwner
  // correctly returns 1.0, regardless of any stale `ownershipPercent`
  // value left over from a previous JT/TIC mode.
  if (property.ownershipMode === "sole") {
    const co = (property.coOwners ?? [])[0];
    if (co && co.sharePercent === 100 && co.householdMemberId) {
      return co.householdMemberId === lensMemberId ? 1.0 : 0;
    }
    // Legacy default: sole means primary owns 100%.
    return lensMemberId === "primary" ? 1.0 : 0;
  }

  if (lensMemberId === "primary") {
    return (property.ownershipPercent ?? 100) / 100;
  }
  const allocation = lossAllocationByMember(property, structure, profile, 1)
    .find((a) => a.householdMemberId === lensMemberId);
  return allocation?.share ?? 0;
}

// ─── computeLensAllocatedLoss (Phase 4a) ─────────────────────────────────────

// Gross rent annualisation. Mirrors the buildPortfolioContext.ts pattern but
// stays file-local to avoid a circular import of `annualIncome`/`annualExpenses`
// (which are themselves file-local in buildPortfolioContext.ts).
function _grossAnnualRent(p: PropertyRecord): number {
  if (p.incomeRows && p.incomeRows.length > 0) {
    return p.incomeRows.reduce((sum, r) => {
      const mult =
        r.frequency === "weekly" ? 52 :
        r.frequency === "fortnightly" ? 26 :
        r.frequency === "monthly" ? 12 :
        1;
      return sum + r.amount * mult;
    }, 0);
  }
  return (p.weeklyRent ?? 0) * 52;
}

function _grossAnnualExpenses(p: PropertyRecord): number {
  if (p.expenseRows && p.expenseRows.length > 0) {
    return filterDeductibleExpenseRows(p.expenseRows).reduce((sum, r) => {
      const mult =
        r.frequency === "weekly" ? 52 :
        r.frequency === "fortnightly" ? 26 :
        r.frequency === "monthly" ? 12 :
        1;
      return sum + r.amount * mult;
    }, 0);
  }
  return (
    (p.annualRates ?? 0) +
    (p.annualInsurance ?? 0) +
    (p.annualStrata ?? 0) +
    (p.annualPropertyManagement ?? 0) +
    (p.annualMaintenance ?? 0) +
    (p.annualLandTax ?? 0) +
    (p.monthlyExpenses ?? 0) * 12
  );
}

/**
 * Sums the rental loss allocated to a specific household member ("primary"
 * or a HouseholdMember.id) across all personal-entity properties.
 *
 * Per-property GROSS loss is `rent − opex − interest` annualised (no
 * ownership factor applied — the GROSS loss is what's allocated, then
 * `lossAllocationByMember` splits it per the property's ownership mode).
 * Properties with non-negative net rental contribute zero loss.
 *
 * Trust / SMSF / Company entities contribute zero — `lossAllocationByMember`
 * returns `[]` for those by L39 (loss quarantined inside the entity).
 *
 * Used by Phase 4b/4c (TaxPage person picker, Dashboard stacked tax cards,
 * AI context per-adult tax block) to drive `personalTaxSavingFromLoss(loss,
 * resolveLensProfile(profile, lensMemberId))`.
 *
 * @example
 * // 2-property household: 1 sole-Angus + 1 JT-50/50 (Angus + Nicole), each
 * // losing $10k/yr.
 * computeLensAllocatedLoss(props, structures, profile, "primary") === 15000
 * // sole: $10k all to primary; JT: $5k to primary.
 * computeLensAllocatedLoss(props, structures, profile, "hm-nicole") === 5000
 * // sole: $0 to Nicole; JT: $5k to Nicole.
 */
export function computeLensAllocatedLoss(
  properties: PropertyRecord[],
  structures: Structure[],
  profile: InvestorProfile,
  lensMemberId: string,
): number {
  let total = 0;
  for (const p of properties) {
    const structure = structures.find((s) => s.id === p.structureId);
    const grossNetRental =
      _grossAnnualRent(p) - _grossAnnualExpenses(p) - annualLoanInterest(p);
    if (grossNetRental >= 0) continue;
    const grossLoss = -grossNetRental;
    const allocations = lossAllocationByMember(p, structure, profile, grossLoss);
    for (const alloc of allocations) {
      if (alloc.householdMemberId === lensMemberId) {
        total += alloc.allocatedLoss;
      }
    }
  }
  return total;
}

// ─── applyPAYGPatch (Phase 4b — write helper) ────────────────────────────────

/**
 * Pure helper for routing PAYG state writes to either the primary investor
 * (profile root) or a household member (inside the household_members JSONB).
 * Used by TaxPage's per-person picker so the same patch shape works for both
 * primary and non-primary lens.
 *
 * Returns the updated InvestorProfile, OR `null` when the lens member id
 * doesn't map to any existing member (defensive — e.g. user deleted the
 * member mid-session). Caller treats `null` as a no-op rather than calling
 * `onUpdateProfile` with a malformed shape.
 */
export function applyPAYGPatch(
  profile: InvestorProfile,
  lensMemberId: string,
  patch: Partial<{
    paygVariationApproved: boolean;
    paygVariationOverride: number | undefined;
    paygWithheld: number | undefined;
  }>,
): InvestorProfile | null {
  if (lensMemberId === "primary") {
    return { ...profile, ...patch };
  }
  const memberExists = (profile.householdMembers ?? []).some(
    (m) => m.id === lensMemberId,
  );
  if (!memberExists) return null;
  const updatedMembers = (profile.householdMembers ?? []).map((m) =>
    m.id === lensMemberId ? { ...m, ...patch } : m,
  );
  return { ...profile, householdMembers: updatedMembers };
}

// ─── resolveLensProfile ──────────────────────────────────────────────────────

/**
 * Symmetric inverse of `effectiveGoalProfile` in App.tsx (ARCHITECTURE §13).
 *
 * `effectiveGoalProfile` spreads the logged-in user's PERSONAL fields and
 * overrides the GOAL/STRATEGY fields from the portfolio owner, so a shared
 * portfolio always shows the household's goal regardless of which user is
 * logged in.
 *
 * `resolveLensProfile` does the opposite for tax modelling: spreads the
 * primary investor profile and overrides the TAX fields from a chosen
 * household member, so the Tax-page person picker can model "what's
 * Nicole's tax position on this loss" without having to spawn a parallel
 * profile object.
 *
 * - lensMemberId === "primary" → returns the profile unchanged (identity)
 * - lensMemberId matches a HouseholdMember.id → spread profile, override
 *   annualPreTaxIncome, marginalTaxRate, medicareExempt, medicareReduction,
 *   hasPrivateHospitalCover, medicareLevy, paygVariationApproved,
 *   paygVariationOverride, paygWithheld from the member.
 * - unknown id → safe fallback to the primary profile (rather than
 *   throw or return a malformed object).
 *
 * Note on hasHECS: HouseholdMember does not (yet) carry a hasHECS field.
 * Phase 1 added only paygVariationApproved/Override/Withheld. Until that
 * lands, the lens member's hasHECS inherits from the primary investor —
 * known gap. The Tax-page person picker should flag this when relevant.
 */
export function resolveLensProfile(
  profile: InvestorProfile,
  lensMemberId: string,
): InvestorProfile {
  if (lensMemberId === "primary") return profile;

  const member = (profile.householdMembers ?? []).find(
    (m) => m.id === lensMemberId,
  );
  if (!member) return profile;

  return {
    ...profile,
    annualPreTaxIncome: member.annualPreTaxIncome ?? profile.annualPreTaxIncome,
    marginalTaxRate: member.marginalTaxRate ?? profile.marginalTaxRate,
    medicareExempt: member.medicareExempt ?? profile.medicareExempt,
    medicareReduction: member.medicareReduction ?? profile.medicareReduction,
    hasPrivateHospitalCover:
      member.hasPrivateHospitalCover ?? profile.hasPrivateHospitalCover,
    medicareLevy: member.medicareLevy ?? profile.medicareLevy,
    paygVariationApproved:
      member.paygVariationApproved ?? profile.paygVariationApproved,
    paygVariationOverride:
      member.paygVariationOverride ?? profile.paygVariationOverride,
    paygWithheld: member.paygWithheld ?? profile.paygWithheld,
    // hasHECS NOT overridden — see JSDoc note. Inherits from primary until
    // HouseholdMember gains the field in a future schema migration.
  };
}
